import { Component, Input } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs';
import { UserOrgService } from '@pics-core/rbac-users';
import { AlertService } from '@pics-core/rbac-users';
import { CommonModule } from '@angular/common';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { TooltipModule } from 'primeng/tooltip';
// import { RegistrationService } from '@app/registration/registration.service';

@Component({
  selector: 'app-user-additional-fields',
   standalone: true,
  templateUrl: './user-additional-fields.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DropdownModule,
    InputTextModule,
    MultiSelectModule,
    TooltipModule
  ],
})
export class UserAdditionalFieldsComponent {
    userid: boolean;
    userFormEvents$: Subject<any>;
    userList: any[] = [];
    filteredUserList: any[] = [];
    @Input()
    set data(val: any) {
        if (val) {
            this._data = val.this;
            if (this._data.userFormEvents$) {
                this.userFormEvents$ = this._data.userFormEvents$;
            }
            if (this._data.userForm) {
                this.initializeExtension();
            }
            if (val.userData) {
                this.patchExtensionData(val.userData);
            }
            // Filter roles after data is set and roles are available
            if (this.isFingerprintAdmin && this._data.userRoles && this._data.userRoles.length > 0) {
                this.filterRolesForFingerprintAdmin();
            }
            if (this.isEppAdmin && this._data.userRoles && this._data.userRoles.length > 0) {
                this.filterRolesForEppAdmin();
            }
            if (this.isLeaAdmin && this._data.userRoles && this._data.userRoles.length > 0) {
                this.filterRolesForLeaAdmin();
            }
        }
    }
    get data(): any {
        return this._data;
    }
    _data: any;
    suffixList: any[] = [];
    providerList: any[] = [];
    districtList: any[] = [];
    genericMessages: any[] = [];
    genericMessage: any = {};
    isFingerprintAdmin: boolean = false;
    isEppAdmin: boolean = false;
    isLeaAdmin: boolean = false;
    fingerprintAdminOrganization: string = '';
    eppAdminProvider: string = '';
    leaAdminOrganization: string = '';
    leaAdminDistrict: any[] = [];
    loggedInUserId: string = '';
    rolesFiltered: boolean = false; // Track if roles have been filtered
    readonly ROLE_TYPE_FINGERPRINT = '5';
    readonly ROLE_TYPE_EPP = '6';
    readonly ROLE_TYPE_APPLICANT = '7';
    readonly ROLE_TYPE_LEA = '8';
    readonly FINGERPRINT_ROLE_IDS = [447, 448]; // Fingerprint related roles
    readonly EPP_ADMIN_ROLE_ID = 472; // EPP Admin role
    readonly LEA_ROLE_IDS = [445, 446]; // LEA related roles
    readonly LEA_ADMIN_ROLE_ID = 446; // LEA Admin role
    recoveryEmailExists: boolean = false;
    lastCheckedEmail: string = '';
    lastCheckedRecoveryEmail: string = '';
    isCheckingEmail: boolean = false;
    isCheckingRecoveryEmail: boolean = false;
    parentMethodsOverridden: boolean = false;
    rolesIntercepted: boolean = false;
    districtConflict: boolean = false;
    districtConflictNames: string[] = [];
    districtRestrictedConflict: boolean = false;
    districtRestrictedFirstName: string = '';
    districtRestrictedAssociatedNames: string[] = [];
    isCheckingDistrict: boolean = false;
    destroy$ = new Subject<void>();

    constructor(
        private userService: UserOrgService,
        private alertService: AlertService,
        private fb: FormBuilder,
        // private registrationService: RegistrationService
    ) { }

    /**
     * Get the logged-in user's role ID from sessionStorage.
     * Checks 'role_id' first, then falls back to user.userroles[0].id
     */
    private getLoggedInRoleId(): string | null {
        let roleId = sessionStorage.getItem('role_id');
        if (!roleId) {
            try {
                const user = JSON.parse(sessionStorage.getItem('user'));
                if (user && user.userroles && user.userroles.length > 0) {
                    roleId = String(user.userroles[0].id);
                }
            } catch (e) { }
        }
        return roleId;
    }

    ngOnInit() {
        // Initialization handled in data setter
    }

    ngAfterViewInit() {
        // Fallback: if roles still not filtered after view init, refilter
        if (this.isFingerprintAdmin || this.isEppAdmin || this.isLeaAdmin) {
            setTimeout(() => {
                this.refilterRoles();
                this.repatchAdminFields();
            }, 500);
        }
    }

    initializeExtension() {
         if (!this._data.userForm.contains('additionalFields')) {
    this._data.userForm.addControl(
      'additionalFields',
      this.fb.group({
        cd_suffix: ['', Validators.required],
        recovery_email: ['', Validators.required],
        organization: [''],
        provider: [''],
        district: [[]],
        cd_role_type: ['']
      })
    );
  }
        this.addDynamicControls();
        this.checkForFingerprintAdmin();
        this.checkForEppAdmin();
        this.checkForLeaAdmin();
        this.interceptRolesLoading();
        this.loadSuffixList();
        this.loadProviderList();
        this.loadDistrictList();
        this.loadGenericMessages();
        this.setupValidators();
        this.setupFormListeners();
        this.overrideParentMethods();
    }

    addDynamicControls() {
        const additionalFields = this._data.userForm.get('additionalFields') as FormGroup;
        if (additionalFields) {
            if (!additionalFields.contains('cd_suffix')) additionalFields.addControl('cd_suffix', new FormControl(''));
            if (!additionalFields.contains('recovery_email')) additionalFields.addControl('recovery_email', new FormControl(''));
            if (!additionalFields.contains('organization')) additionalFields.addControl('organization', new FormControl(''));
            if (!additionalFields.contains('provider')) additionalFields.addControl('provider', new FormControl(''));
            if (!additionalFields.contains('district')) additionalFields.addControl('district', new FormControl([]));
            if (!additionalFields.contains('cd_role_type')) additionalFields.addControl('cd_role_type', new FormControl(''));
        }
    }

    patchExtensionData(userData: any) {
        if (this._data.userForm && this._data.userForm.get('additionalFields')) {
            // Use setTimeout to ensure controls are fully initialized
            setTimeout(() => {
                const additionalFields = this._data.userForm.get('additionalFields') as FormGroup;
                if (additionalFields) {
                    // Enable all fields temporarily to allow patching
                    additionalFields.get('cd_suffix')?.enable({ emitEvent: false });
                    additionalFields.get('recovery_email')?.enable({ emitEvent: false });
                    additionalFields.get('organization')?.enable({ emitEvent: false });
                    additionalFields.get('provider')?.enable({ emitEvent: false });
                    additionalFields.get('district')?.enable({ emitEvent: false });

                    // Patch the values
                    const patchData: any = {};
                    if (userData.cd_suffix !== undefined && userData.cd_suffix !== null) {
                        patchData.cd_suffix = userData.cd_suffix;
                    }
                    if (userData.recovery_email !== undefined && userData.recovery_email !== null) {
                        patchData.recovery_email = userData.recovery_email;
                    }
                    if (userData.organization !== undefined && userData.organization !== null) {
                        patchData.organization = userData.organization;
                    }
                    if (userData.provider !== undefined && userData.provider !== null) {
                        patchData.provider = userData.provider;
                    }
                    if (userData.district !== undefined && userData.district !== null) {
                        patchData.district = Array.isArray(userData.district) ? userData.district : [];
                    }

                    additionalFields.patchValue(patchData, { emitEvent: false });

                    // Update validation and field states after patching
                    setTimeout(() => {
                        this.updateSuffixAndRecoveryEmailValidation(this._data.userForm.get('userroles')?.value);
                        this.updateOrganizationValidation(this._data.userForm.get('userroles')?.value);
                        this.updateProviderValidation(this._data.userForm.get('userroles')?.value);
                        this.updateDistrictValidation(this._data.userForm.get('userroles')?.value);
                    }, 50);
                }
            }, 100);
        }
    }

    checkForFingerprintAdmin() {
        const roleId = this.getLoggedInRoleId();
        this.isFingerprintAdmin = roleId === '448';

        if (this.isFingerprintAdmin) {
            this.loggedInUserId = sessionStorage.getItem('id') || '';
            if (this.loggedInUserId) {
                this.getFingerprintAdminInfo();
            }
            // Role filtering is now done in the data setter after roles are loaded
            setTimeout(() => {
                const additionalFields = this._data.userForm.get('additionalFields');
                if (additionalFields) {
                    additionalFields.get('cd_suffix')?.enable();
                    additionalFields.get('recovery_email')?.disable();
                    additionalFields.get('organization')?.disable();
                    additionalFields.patchValue({ cd_role_type: this.ROLE_TYPE_FINGERPRINT }, { emitEvent: false });
                }
            }, 100);
        }
    }

    /**
     * Intercept the parent component's userRoles property so that
     * whenever roles are loaded (or reloaded) from the API, we
     * automatically filter them for fingerprint/EPP admin.
     * This eliminates race conditions where roles load after our
     * initial filter checks have already run.
     */
    interceptRolesLoading() {
        if (this.rolesIntercepted) return;
        if (!this._data) return;
        if (!this.isFingerprintAdmin && !this.isEppAdmin && !this.isLeaAdmin) return;

        // If roles are already available, filter immediately
        if (this._data.userRoles && this._data.userRoles.length > 0) {
            this.refilterRoles();
        }

        // Intercept the userRoles setter to auto-filter when library sets roles
        const self = this;
        let _currentRoles = this._data.userRoles;

        Object.defineProperty(this._data, 'userRoles', {
            get() { return _currentRoles; },
            set(value) {
                _currentRoles = value;
                if (value && value.length > 0) {
                    // Use setTimeout(0) to let the parent finish its processing first
                    setTimeout(() => self.refilterRoles(), 0);
                }
            },
            configurable: true,
            enumerable: true
        });

        this.rolesIntercepted = true;
    }

    /**
     * Override parent library component methods so that after add/update/delete/cancel,
     * the user list filtering, role filtering, and org/provider patching are re-applied.
     */
    overrideParentMethods() {
        if (this.parentMethodsOverridden) return;
        if (!this._data) return;
        const self = this;

        // Always override onClickAddUser so controls are re-attached for all users
        const originalOnClickAddUser = this._data.onClickAddUser?.bind(this._data);
        if (originalOnClickAddUser) {
            this._data.onClickAddUser = function() {
                originalOnClickAddUser();
                self.reinitializeAfterFormReset();
            };
        }

        if (!this.isFingerprintAdmin && !this.isEppAdmin && !this.isLeaAdmin) {
            this.parentMethodsOverridden = true;
            return;
        }

        // Override getUserList for filtered user list
        this._data.getUserList = function(_key?: any) {
            self.getUserList();
        };

        // Override cancel to re-patch admin fields after form reset
        const originalCancel = this._data.cancel?.bind(this._data);
        if (originalCancel) {
            this._data.cancel = function(changeFlag: any) {
                originalCancel(changeFlag);
                self.reapplyAdminSettings();
            };
        }

        // Override cancelUser to re-patch admin fields
        const originalCancelUser = this._data.cancelUser?.bind(this._data);
        if (originalCancelUser) {
            this._data.cancelUser = function() {
                originalCancelUser();
                self.reapplyAdminSettings();
            };
        }

        this.parentMethodsOverridden = true;
    }

    /**
     * Re-apply organization/provider patch and role filtering after form reset
     */
    reapplyAdminSettings() {
        setTimeout(() => {
            this.repatchAdminFields();
            this.refilterRoles();
        }, 150);
    }

    /**
     * Re-patch organization/provider fields for admin users after form reset
     */
    repatchAdminFields() {
        const additionalFields = this._data.userForm?.get('additionalFields');
        if (!additionalFields) return;

        if (this.isFingerprintAdmin && this.fingerprintAdminOrganization) {
            additionalFields.patchValue(
                { organization: this.fingerprintAdminOrganization },
                { emitEvent: false }
            );
            additionalFields.get('organization')?.disable({ emitEvent: false });
        }

        if (this.isEppAdmin && this.eppAdminProvider) {
            additionalFields.patchValue(
                { provider: this.eppAdminProvider },
                { emitEvent: false }
            );
            additionalFields.get('provider')?.disable({ emitEvent: false });
        }

        // if (this.isLeaAdmin && this.leaAdminOrganization) {
        //     additionalFields.patchValue(
        //         { organization: this.leaAdminOrganization },
        //         { emitEvent: false }
        //     );
        //     additionalFields.get('organization')?.disable({ emitEvent: false });
        // }

        if (this.isLeaAdmin && this.leaAdminDistrict && this.leaAdminDistrict.length > 0) {
            additionalFields.patchValue(
                { district: this.leaAdminDistrict },
                { emitEvent: false }
            );
            additionalFields.get('district')?.disable({ emitEvent: false });
        }
    }

    /**
     * Re-initialize additionalFields after parent creates a new form (onClickAddUser)
     */
    reinitializeAfterFormReset() {
        if (!this._data || !this._data.userForm) return;

        // initializeForm() creates an empty additionalFields group, so we always
        // need to re-add the dynamic controls into it.
        if (!this._data.userForm.contains('additionalFields')) {
            this._data.userForm.addControl(
                'additionalFields',
                this.fb.group({
                    cd_suffix: [''],
                    recovery_email: [''],
                    organization: [''],
                    provider: [''],
                    district: [[]],
                    cd_role_type: ['']
                })
            );
        } else {
            this.addDynamicControls();
        }

        // Clear any leftover values for a fresh Add User form
        (this._data.userForm.get('additionalFields') as FormGroup)?.reset();

        // Re-setup listeners and validators on the new form
        this.destroy$.next();
        this.setupFormListeners();
        this.setupValidators();

        // Admin-specific: re-apply org/provider patches and role filtering
        this.reapplyAdminSettings();
    }

    /**
     * Re-filter roles if they were reset to unfiltered state
     */
    refilterRoles() {
        if (!this._data.userRoles || this._data.userRoles.length === 0) return;

        if (this.isFingerprintAdmin) {
            const hasUnfilteredRoles = this._data.userRoles.some(
                role => role.cd_role_type !== this.ROLE_TYPE_FINGERPRINT
            );
            if (hasUnfilteredRoles) {
                this._data.userRoles = this._data.userRoles.filter(
                    role => role.cd_role_type === this.ROLE_TYPE_FINGERPRINT
                );
            }
        }

        if (this.isEppAdmin) {
            const hasUnfilteredRoles = this._data.userRoles.some(
                role => role.cd_role_type !== this.ROLE_TYPE_EPP
            );
            if (hasUnfilteredRoles) {
                this._data.userRoles = this._data.userRoles.filter(
                    role => role.cd_role_type === this.ROLE_TYPE_EPP
                );
            }
        }

        if (this.isLeaAdmin) {
            const hasUnfilteredRoles = this._data.userRoles.some(
                role => role.cd_role_type !== this.ROLE_TYPE_LEA
            );
            if (hasUnfilteredRoles) {
                this._data.userRoles = this._data.userRoles.filter(
                    role => role.cd_role_type === this.ROLE_TYPE_LEA
                );
            }
        }
    }

    getUserList() {
        const orgId = this._data?.orgId || this._data?.RBACORG?.orgID;
        if (!orgId) return;

        this.userService.getAllUserOrgList(orgId).subscribe(
            (res: any) => {
                if (res && res.data) {
                    let userList = res.data;

                    // Filter user list for fingerprint admin
                    if (this.isFingerprintAdmin && this.fingerprintAdminOrganization) {
                        userList = userList.filter(user => {
                            const userOrg = user.additionalFields?.organization || user.organization;
                            const userRoleType = user.additionalFields?.cd_role_type;
                            const orgMatch = userOrg === this.fingerprintAdminOrganization;
                            // If cd_role_type is stored, use it to distinguish; otherwise fall back to org-only
                            return userRoleType
                                ? orgMatch && userRoleType === this.ROLE_TYPE_FINGERPRINT
                                : orgMatch;
                        });
                    }

                    // Filter user list for EPP admin
                    if (this.isEppAdmin && this.eppAdminProvider) {
                        userList = userList.filter(user => {
                            const userProvider = user.additionalFields?.provider || user.provider;
                            const userRoleType = user.additionalFields?.cd_role_type;
                            const providerMatch = userProvider === this.eppAdminProvider;
                            return userRoleType
                                ? providerMatch && userRoleType === this.ROLE_TYPE_EPP
                                : providerMatch;
                        });
                    }

                    // Filter user list for LEA admin
                    if (this.isLeaAdmin && this.leaAdminDistrict && this.leaAdminDistrict.length > 0) {
                        userList = userList.filter(user => {
                            // const userOrg = user.additionalFields?.organization || user.organization;
                            const userDistrictData = user.additionalFields?.district || user.district;
                            const userRoleType = user.additionalFields?.cd_role_type;
                            // const orgMatch = userOrg === this.leaAdminOrganization;
                            const userDistricts = Array.isArray(userDistrictData) ? userDistrictData : (userDistrictData ? [userDistrictData] : []);
                            const districtMatch = userDistricts.some(district => this.leaAdminDistrict.includes(district));
                            // return userRoleType
                            //     ? orgMatch && userRoleType === this.ROLE_TYPE_LEA
                            //     : orgMatch;
                              return userRoleType
                                ? districtMatch && userRoleType === this.ROLE_TYPE_LEA
                                : districtMatch;
                        });
                    }

                    // Update parent component's lists
                    this._data.userList = userList;
                    this._data.filteredUserList = userList;
                }
            },
            (error: any) => {
                console.log('Failed to fetch user list', error);
                this.alertService.error('Failed to fetch user list');
            }
        );
    }

    getFingerprintAdminInfo() {
        this.userService.getAllUserList(this.loggedInUserId).subscribe(
            (res: any) => {
                if (res && res.data) {
                    // Get organization from additionalFields first, fallback to root level
                    this.fingerprintAdminOrganization = res.data.additionalFields?.organization || res.data.organization || '';

                    if (this._data.userForm && this.fingerprintAdminOrganization) {
                        this._data.userForm.get('additionalFields')?.patchValue({ organization: this.fingerprintAdminOrganization }, { emitEvent: false });
                        this._data.userForm.get('additionalFields.organization')?.disable();
                    }
                    // Filter roles immediately when admin info is ready
                    if (this._data?.userRoles && this._data.userRoles.length > 0) {
                        this.filterRolesForFingerprintAdmin();
                    }
                    // Load and filter user list after getting admin organization
                    this.getUserList();
                }
            },
            (error: any) => console.log('Failed to get admin info', error)
        );
    }

    filterRolesForFingerprintAdmin() {
        if (!this._data.userRoles) {
            return;
        }

        // Prevent filtering multiple times
        if (this.rolesFiltered) {
            return;
        }

        // Filter roles to show only fingerprint-related roles (role type 5)
        this._data.userRoles = this._data.userRoles.filter(role => {
            return role.cd_role_type === this.ROLE_TYPE_FINGERPRINT;
        });

        this.rolesFiltered = true; // Mark as filtered
    }

    checkForEppAdmin() {
        const roleId = this.getLoggedInRoleId();
        this.isEppAdmin = roleId === '472';

        if (this.isEppAdmin) {
            this.loggedInUserId = sessionStorage.getItem('id') || '';
            if (this.loggedInUserId) {
                this.getEppAdminInfo();
            }
            // Role filtering is now done in the data setter after roles are loaded
            setTimeout(() => {
                const additionalFields = this._data.userForm.get('additionalFields');
                if (additionalFields) {
                    additionalFields.get('cd_suffix')?.enable();
                    additionalFields.get('recovery_email')?.disable();
                    additionalFields.get('provider')?.disable();
                    additionalFields.patchValue({ cd_role_type: this.ROLE_TYPE_EPP }, { emitEvent: false });
                }
            }, 100);
        }
    }

    getEppAdminInfo() {
        this.userService.getAllUserList(this.loggedInUserId).subscribe(
            (res: any) => {
                if (res && res.data) {
                    // Get provider from additionalFields first, fallback to root level
                    this.eppAdminProvider = res.data.additionalFields?.provider || res.data.provider || '';

                    if (this._data.userForm && this.eppAdminProvider) {
                        this._data.userForm.get('additionalFields')?.patchValue({ provider: this.eppAdminProvider }, { emitEvent: false });
                        this._data.userForm.get('additionalFields.provider')?.disable();
                    }
                    // Filter roles immediately when admin info is ready
                    if (this._data?.userRoles && this._data.userRoles.length > 0) {
                        this.filterRolesForEppAdmin();
                    }
                    // Load and filter user list after getting admin provider
                    this.getUserList();
                }
            },
            (error: any) => console.log('Failed to get EPP admin info', error)
        );
    }

    filterRolesForEppAdmin() {
        if (!this._data.userRoles) {
            return;
        }

        // Prevent filtering multiple times
        if (this.rolesFiltered) {
            return;
        }

        // Filter roles to show only EPP-related roles (role type 6)
        this._data.userRoles = this._data.userRoles.filter(role => {
            return role.cd_role_type === this.ROLE_TYPE_EPP;
        });

        this.rolesFiltered = true; // Mark as filtered
    }

    checkForLeaAdmin() {
        const roleId = this.getLoggedInRoleId();
        this.isLeaAdmin = roleId === '446';

        if (this.isLeaAdmin) {
            this.loggedInUserId = sessionStorage.getItem('id') || '';
            if (this.loggedInUserId) {
                this.getLeaAdminInfo();
            }
            // Role filtering is now done in the data setter after roles are loaded
            setTimeout(() => {
                const additionalFields = this._data.userForm.get('additionalFields');
                if (additionalFields) {
                    additionalFields.get('cd_suffix')?.enable();
                    additionalFields.get('recovery_email')?.disable();
                    // additionalFields.get('organization')?.disable();
                    additionalFields.get('district')?.disable();
                    additionalFields.patchValue({ cd_role_type: this.ROLE_TYPE_LEA }, { emitEvent: false });
                }
            }, 100);
        }
    }

    getLeaAdminInfo() {
        this.userService.getAllUserList(this.loggedInUserId).subscribe(
            (res: any) => {
                if (res && res.data) {
                    // Get organization from additionalFields first, fallback to root level
                    // this.leaAdminOrganization = res.data.additionalFields?.organization || res.data.organization || '';

                    // Get district from additionalFields first, fallback to root level
                    const districtData = res.data.additionalFields?.district || res.data.district;
                    this.leaAdminDistrict = Array.isArray(districtData) ? districtData : (districtData ? [districtData] : []);

                    // if (this._data.userForm && this.leaAdminOrganization) {
                    //     this._data.userForm.get('additionalFields')?.patchValue({ organization: this.leaAdminOrganization }, { emitEvent: false });
                    //     this._data.userForm.get('additionalFields.organization')?.disable();
                    // }

                    if (this._data.userForm && this.leaAdminDistrict.length > 0) {
                        this._data.userForm.get('additionalFields')?.patchValue({ district: this.leaAdminDistrict }, { emitEvent: false });
                        this._data.userForm.get('additionalFields.district')?.disable();
                    }
                    // Filter roles immediately when admin info is ready
                    if (this._data?.userRoles && this._data.userRoles.length > 0) {
                        this.filterRolesForLeaAdmin();
                    }
                    // Load and filter user list after getting admin organization
                    this.getUserList();
                }
            },
            (error: any) => console.log('Failed to get LEA admin info', error)
        );
    }

    filterRolesForLeaAdmin() {
        if (!this._data.userRoles) {
            return;
        }

        // Prevent filtering multiple times
        if (this.rolesFiltered) {
            return;
        }

        // Filter roles to show only LEA-related roles (role type 8)
        this._data.userRoles = this._data.userRoles.filter(role => {
            return role.cd_role_type === this.ROLE_TYPE_LEA;
        });

        this.rolesFiltered = true; // Mark as filtered
    }

    loadSuffixList() {
        if (this._data && this._data.httpService) {
            this._data.httpService.get('/domain/lookup?requestType=SUFFIX').subscribe(
                (res: any) => {
                    this.suffixList = res.data;
                    const noneIndex = this.suffixList.findIndex(item => item.value === 'None');
                    if (noneIndex !== -1) {
                        const noneItem = this.suffixList.splice(noneIndex, 1)[0];
                        this.suffixList.push(noneItem);
                    }
                },
                (err: any) => console.log('Failed to load suffix list:', err)
            );
        }
    }

    loadProviderList() {
        if (this._data && this._data.httpService) {
            this._data.httpService.get('/domain/lookup?requestType=EPP-PROVIDER').subscribe(
                (res: any) => {
                    this.providerList = res.data;
                },
                (err: any) => console.log('Failed to load provider list:', err)
            );
        }
    }

    loadDistrictList() {
        if (this._data && this._data.httpService) {
            this._data.httpService.get('/domain/lookup?requestType=DISTRICT').subscribe(
                (res: any) => {
                    this.districtList = res.data;
                },
                (err: any) => console.log('Failed to load district list:', err)
            );
        }
    }

    loadGenericMessages() {
        // Hydrate from localStorage cache first for instant tooltip availability
        const cachedMessages = window.localStorage.getItem('genericMessages');
        if (cachedMessages) {
            try {
                this.genericMessages = JSON.parse(cachedMessages);
                this.mapGenericMessages();
            } catch (e) {
                // Ignore parse errors and fall through to a fresh fetch
            }
        }

        // Always fetch a fresh copy from the Domain Parameter lookup
        if (this._data && this._data.httpService) {
            this._data.httpService.get('/domain/lookup?requestType=GENERIC MESSAGES').subscribe(
                (res: any) => {
                    this.genericMessages = res.data || [];
                    this.mapGenericMessages();
                    window.localStorage.setItem('genericMessages', JSON.stringify(this.genericMessages));
                },
                (err: any) => console.log('Failed to load generic messages:', err)
            );
        }
    }

    private mapGenericMessages() {
        this.genericMessage = {};
        this.genericMessages.forEach(message => {
            if (message.key && message.value !== undefined) {
                this.genericMessage[message.key] = message.value;
            }
        });
    }

    setupValidators() {
        const emailControl = this._data.userForm.get('email');
        if (emailControl) {
            emailControl.setValidators([Validators.required, this.emailValidation()]);
        }

        const roleControl = this._data.userForm.get('userroles');
        if (roleControl) {
            roleControl.setValidators([this.roleValidation()]);
        }

        const organizationControl = this._data.userForm.get('additionalFields.organization');
        if (organizationControl) {
            this.updateOrganizationValidation(this._data.userForm.get('userroles')?.value);
        }

        const providerControl = this._data.userForm.get('additionalFields.provider');
        if (providerControl) {
            this.updateProviderValidation(this._data.userForm.get('userroles')?.value);
        }

        const districtControl = this._data.userForm.get('additionalFields.district');
        if (districtControl) {
            this.updateDistrictValidation(this._data.userForm.get('userroles')?.value);
        }

        const suffixControl = this._data.userForm.get('additionalFields.cd_suffix');
        if (suffixControl) {
            this.updateSuffixValidation(this._data.userForm.get('userroles')?.value);
        }

        const recoveryEmail = this._data.userForm.get('additionalFields.recovery_email');
        if (recoveryEmail) {
            recoveryEmail.setValidators([this.validateRecoveryEmail()]);
        }
    }
setupFormListeners() {
    const roleControl = this._data.userForm.get('userroles');
  if (!roleControl) return;
  roleControl.valueChanges
    .pipe(takeUntil(this.destroy$))
    .subscribe(roleId => {
      this.onRoleChange(roleId);
    });

    // Clear recovery email error when the field value changes
    const recoveryEmailControl = this._data.userForm.get('additionalFields.recovery_email');
    if (recoveryEmailControl) {
        recoveryEmailControl.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.recoveryEmailExists = false;
                this.lastCheckedRecoveryEmail = ''; // Clear the cache to allow re-checking the same email
            });
    }

    // Validate district assignment on selection change
    const districtControl = this._data.userForm.get('additionalFields.district');
    if (districtControl) {
        districtControl.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((selectedDistricts: any[]) => {
                this.validateDistrictAssignment(selectedDistricts);
            });
    }
}

    onRoleChange(roleId: any) {
        this.updateOrganizationValidation(roleId);
        this.updateProviderValidation(roleId);
        this.updateDistrictValidation(roleId);
        this.updateSuffixAndRecoveryEmailValidation(roleId);
        this.updateSuffixValidation(roleId);

        // Sync cd_role_type into additionalFields so it persists to DB on save
        const selectedRoleType = this.getUserRoleType(roleId);
        const additionalFields = this._data.userForm.get('additionalFields');
        if (additionalFields && selectedRoleType) {
            additionalFields.patchValue({ cd_role_type: selectedRoleType }, { emitEvent: false });
        }

        if (selectedRoleType === this.ROLE_TYPE_APPLICANT) {
            if (!this._data?.userid) {
                this._data.userForm.get('additionalFields.cd_suffix')?.enable();
                this._data.userForm.get('additionalFields.recovery_email')?.enable();
            }
        }
    }

    updateOrganizationValidation(roleId: any) {
        const organizationControl = this._data.userForm.get('additionalFields.organization');
        if (!organizationControl) return;

        if (this.isFingerprintAdmin ) {
            organizationControl.setValidators([Validators.required, this.organizationValidation()]);
            organizationControl.updateValueAndValidity({ emitEvent: false });
            return;
        }


        const selectedRole = this._data.userRoles?.find(role => role.id === roleId);
        if (selectedRole && (selectedRole.cd_role_type === this.ROLE_TYPE_FINGERPRINT )) {
            organizationControl.setValidators([Validators.required, this.organizationValidation()]);
            organizationControl.enable({ emitEvent: false });
        } else {
            organizationControl.setValidators([this.organizationValidation()]);
            organizationControl.disable({ emitEvent: false });
        }
        organizationControl.updateValueAndValidity({ emitEvent: false });
    }

    updateProviderValidation(roleId: any) {
        const providerControl = this._data.userForm.get('additionalFields.provider');
        if (!providerControl) return;

        if (this.isEppAdmin) {
            providerControl.setValidators([Validators.required]);
            providerControl.updateValueAndValidity({ emitEvent: false });
            return;
        }

        const selectedRole = this._data.userRoles?.find(role => role.id === roleId);
        if (selectedRole && selectedRole.cd_role_type === this.ROLE_TYPE_EPP) {
            providerControl.setValidators([Validators.required]);
            providerControl.enable({ emitEvent: false });
        } else {
            providerControl.clearValidators();
            providerControl.disable({ emitEvent: false });
        }
        providerControl.updateValueAndValidity({ emitEvent: false });
    }

    updateDistrictValidation(roleId: any) {
        const districtControl = this._data.userForm.get('additionalFields.district');
        if (!districtControl) return;

        if (this.isLeaAdmin) {
            districtControl.setValidators([Validators.required]);
            districtControl.updateValueAndValidity({ emitEvent: false });
            return;
        }

        const selectedRole = this._data.userRoles?.find(role => role.id === roleId);
        if (selectedRole && selectedRole.cd_role_type === this.ROLE_TYPE_LEA) {
            districtControl.setValidators([Validators.required]);
            districtControl.enable({ emitEvent: false });
        } else {
            districtControl.clearValidators();
            districtControl.disable({ emitEvent: false });
        }

        // District is read-only when editing an existing user
        if (this._data?.userid) {
            districtControl.disable({ emitEvent: false });
        }

        districtControl.updateValueAndValidity({ emitEvent: false });
    }

    updateSuffixValidation(roleId: any) {
        const suffixControl = this._data.userForm.get('additionalFields.cd_suffix');
        if (!suffixControl) return;

        // Suffix is optional but we want to validate if cleared after being filled
        suffixControl.clearValidators();
        suffixControl.updateValueAndValidity({ emitEvent: false });
    }

 updateSuffixAndRecoveryEmailValidation(roleId: any) {
  const additionalFields = this._data.userForm.get('additionalFields') as FormGroup;

  if (!additionalFields) return;

  const suffix = additionalFields.get('cd_suffix');
  const recovery = additionalFields.get('recovery_email');

  if (this.isFingerprintAdmin) {
    suffix?.enable({ emitEvent: false });
    recovery?.disable({ emitEvent: false });
    return;
  }

  if (this.isEppAdmin) {
    suffix?.enable({ emitEvent: false });
    recovery?.disable({ emitEvent: false });
    return;
  }

  if (this.isLeaAdmin) {
    suffix?.enable({ emitEvent: false });
    recovery?.disable({ emitEvent: false });
    return;
  }

  const roleType = this.getUserRoleType(roleId);

  // For applicant role type, enable fields
  if (roleType === this.ROLE_TYPE_APPLICANT) {
    suffix?.enable({ emitEvent: false });
    recovery?.enable({ emitEvent: false });
  } else {
    // For other role types, keep enabled if they have values (edit mode), otherwise disable
    if (suffix?.value) {
      suffix.enable({ emitEvent: false });
    } else {
      suffix?.disable({ emitEvent: false });
    }
    if (recovery?.value) {
      recovery.enable({ emitEvent: false });
    } else {
      recovery?.disable({ emitEvent: false });
    }
  }
}


    getUserRoleType(roleId: any): string | null {
        const role = this.data.userRoles?.find((r: any) => r.id === roleId);
        return role ? role.cd_role_type : null;
    }

    // Validators
    organizationValidation(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) return null;
            const value = control.value.toString().trim();
            if (value.length < 2 || value.length > 60) return { customError: 'Please enter a valid value.' };
            const validPattern = /^[A-Za-z\s.,'&()/-]+$/;
            if (!validPattern.test(value)) return { customError: 'Please enter a valid value.' };
            return null;
        };
    }

    roleValidation(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) {
                return { customError: 'Please select a role.' };
            }
            return null;
        };
    }

    emailValidation(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value || control.value.toString().trim() === '') {
                return { customError: 'Email is required.' };
            }
            const value = control.value.toString().trim();
            if (value.length > 255) return { customError: 'Please enter a valid email address.' };
            const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            if (!emailPattern.test(value)) return { customError: 'Please enter a valid email address.' };
            return null;
        };
    }

    validateRecoveryEmail(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value || control.value.toString().trim() === '') {
                return null; // Recovery email is optional, so return null when empty
            }
            const value = control.value.toString().trim();
            if (value.length > 255) return { customError: 'Please enter a valid email address.' };
            const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            if (!emailPattern.test(value)) return { customError: 'Please enter a valid email address.' };

            const primaryEmail = this._data.userForm.get('email')?.value;
            if (primaryEmail && value.toLowerCase() === primaryEmail.toLowerCase()) {
                return { customError: 'Primary and recovery emails must be different.' };
            }
            return null;
        };
    }

    validateRecoveryEmailSame() {
        const emailControl = this._data.userForm.get('email');
        const recoveryEmailControl = this._data.userForm.get('additionalFields.recovery_email');
        if (!emailControl || !recoveryEmailControl) return null;

        const email = emailControl.value;
        const recoveryEmail = recoveryEmailControl.value;

        if (recoveryEmail && email && recoveryEmail.toLowerCase() === email.toLowerCase()) {
            recoveryEmailControl.setErrors({ 'sameAsEmail': true });
            return { 'sameAsEmail': true };
        } else if (recoveryEmailControl.errors?.['sameAsEmail']) {
            const currentErrors = { ...recoveryEmailControl.errors };
            delete currentErrors['sameAsEmail'];
            recoveryEmailControl.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
        }
        return null;
    }

    checkEmail() {
        // Placeholder
    }

      checkRecoveryEmail() {
    this.validateRecoveryEmailSame();

    const recoveryEmailValue = this._data.userForm.get('additionalFields.recovery_email')?.value?.trim();

    if (!recoveryEmailValue) {
      return;
    }

    if (this.isCheckingRecoveryEmail || this.userid || this.lastCheckedRecoveryEmail === recoveryEmailValue) {
      return;
    }

    this.isCheckingRecoveryEmail = true;
    let request = {
      email: recoveryEmailValue
    };

    // this.registrationService.checkEmail(request).subscribe({
    //   next: response => {
    //     this.isCheckingRecoveryEmail = false;
    //     if (response.data.exists === true) {
    //       this.recoveryEmailExists = true;
    //     } else {
    //       this.recoveryEmailExists = false;
    //     }
    //     this.lastCheckedRecoveryEmail = recoveryEmailValue;
    //   },
    //   error: () => {
    //     this.isCheckingRecoveryEmail = false;
    //     this.recoveryEmailExists = false;
    //     this.lastCheckedRecoveryEmail = recoveryEmailValue;
    //   }
    // });
  }

    validateDistrictAssignment(selectedDistricts: any[]) {
        this.districtConflict = false;
        this.districtConflictNames = [];
        this.districtRestrictedConflict = false;
        this.districtRestrictedFirstName = '';
        this.districtRestrictedAssociatedNames = [];

        const districtControl = this._data.userForm.get('additionalFields.district');

        if (!selectedDistricts || selectedDistricts.length === 0) {
            this.clearDistrictConflictErrors(districtControl);
            return;
        }

        if (!this._data?.httpService) {
            return;
        }

        this.isCheckingDistrict = true;
        // Validate based on the first selected district (existing behavior)
        const firstDistrict = selectedDistricts[0];
        const url = this.buildDistrictValidationUrl(firstDistrict);

        this._data.httpService.get(url).subscribe(
            (res: any) => {
                const districtControl = this._data.userForm.get('additionalFields.district');
                const districtMap: { key: string; name: string }[] = res.data?.districtMap || [];
                const selectedKeys = new Set((selectedDistricts || []).map(String));

                // Associated districts belonging to the first-district group
                const associatedDistricts = districtMap.filter(d => String(d.key) !== String(firstDistrict));

                if (res.data?.conflict) {
                    // If the user picked any district outside the first district's
                    // associated group, show the restriction error regardless of
                    // whether the associated districts have also been selected.
                    const associatedGroupKeys = new Set<string>([
                        String(firstDistrict),
                        ...associatedDistricts.map(d => String(d.key)),
                    ]);
                    const extraDistricts = selectedDistricts.filter(
                        d => !associatedGroupKeys.has(String(d)),
                    );

                    if (extraDistricts.length > 0) {
                        this.districtRestrictedConflict = true;
                        this.districtRestrictedFirstName = this.getDistrictNameByKey(firstDistrict);
                        this.districtRestrictedAssociatedNames = associatedDistricts.map(d => d.name);
                        if (districtControl) {
                            const errors = { ...districtControl.errors };
                            errors['districtRestricted'] = true;
                            delete errors['districtConflict'];
                            districtControl.setErrors(errors);
                        }
                        this.isCheckingDistrict = false;
                        return;
                    }

                    const allAssociatedSelected = associatedDistricts.every(d => selectedKeys.has(String(d.key)));

                    if (!allAssociatedSelected) {

                        this.districtConflict = true;
                        this.districtConflictNames = associatedDistricts
                            .filter(d => !selectedKeys.has(String(d.key)))
                            .map(d => d.name);
                        if (districtControl) {
                            const errors = { ...districtControl.errors };
                            errors['districtConflict'] = true;
                            delete errors['districtRestricted'];
                            districtControl.setErrors(errors);
                        }
                        this.isCheckingDistrict = false;
                        return;
                    }
                }

                // First-district group is satisfied and no out-of-group districts
                // are selected. Clear the primary conflict / restriction errors.
                this.districtConflict = false;
                this.districtConflictNames = [];
                this.districtRestrictedConflict = false;
                this.districtRestrictedFirstName = '';
                this.districtRestrictedAssociatedNames = [];
                this.clearDistrictConflictErrors(districtControl);
                this.isCheckingDistrict = false;
            },
            (error: any) => {
                this.isCheckingDistrict = false;
                console.log('Failed to validate district assignment', error);
            }
        );
    }

    private getDistrictNameByKey(districtKey: any): string {
        const item = this.districtList?.find(
            (d: any) => String(d.key) === String(districtKey),
        );
        return item?.value || item?.name || String(districtKey);
    }

    private buildDistrictValidationUrl(districtKey: any): string {
        const excludeUserId = this._data.userid || '';
        let url = `/domain/validate-district-assignment?districtKeys=${encodeURIComponent(String(districtKey))}`;
        if (excludeUserId) {
            url += `&excludeUserId=${encodeURIComponent(excludeUserId)}`;
        }
        return url;
    }

    private clearDistrictConflictErrors(districtControl: AbstractControl | null) {
        if (!districtControl) return;
        if (districtControl.errors?.['districtConflict'] || districtControl.errors?.['districtRestricted']) {
            const currentErrors = { ...districtControl.errors };
            delete currentErrors['districtConflict'];
            delete currentErrors['districtRestricted'];
            districtControl.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
        }
    }

    get formValidate() {
    return this._data.userForm.controls;
  }

    get additionalFields() {
        return this._data.userForm?.get('additionalFields') as FormGroup;
    }

    onPageNameInput(event: KeyboardEvent, name: string) {
    const input = event.target as HTMLInputElement;
    const startPosition = input.selectionStart;
    const endPosition = input.selectionEnd;

    // Access the control using the passed name
    const pagenameControl = this._data.userForm.get(name);

    if (pagenameControl && pagenameControl.value) {
      const newValue = pagenameControl.value
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      pagenameControl.patchValue(newValue, { emitEvent: false });
      // Restore cursor position to prevent jumping
      input.setSelectionRange(startPosition, endPosition);
    }
  }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
